import React, { FC, useState } from 'react';
import { Grid, TextField, MenuItem, Select, InputLabel, FormControl, Autocomplete, Box, Typography, CircularProgress } from '@mui/material';
import { DateTime } from 'luxon';

import { formatTaxNumber } from 'utils/formatters';
import DateRangePicker from '../../../../../components/DateRangePicker';

type Account = { id: number; businessName: string; cnpj: string };
type Counterparty = { id: number; businessName: string; cnpj: string };

type Props = {
  onApply: (filters: any) => void;
  accounts: Account[];
  counterparties: Counterparty[];
  loadingFilters?: boolean;
};

const REQUEST_STATUS_OPTIONS = [
  { label: 'Todos', value: '' },
  { label: 'Revisão', value: 'PENDING' },
  { label: 'Aprovado', value: 'APPROVED' },
  { label: 'Rejeitado', value: 'REJECTED' },
  { label: 'Expirado', value: 'EXPIRED' },
];

const TRANSACTION_STATUS_OPTIONS = [
  { label: 'Todos', value: '' },
  { label: 'Processando', value: 'PROCESSING' },
  { label: 'Confirmado', value: 'CONFIRMED' },
  { label: 'Erro', value: 'ERROR' },
  { label: 'Cancelado', value: 'CANCELED' },
  { label: 'Enviado ao provedor', value: 'SENT_TO_PROVIDER' },
];

const ManualWithdrawFilters: FC<Props> = ({ onApply, accounts, counterparties, loadingFilters }) => {
  const [filters, setFilters] = useState({
    requestStatus: '',
    transactionStatus: '',
    originAccountId: '',
    counterpartyId: '',
    createdAtFrom: null as DateTime | null,
    createdAtTo: null as DateTime | null,
  });

  const handleChange = (key: string, value: any) => {
    const updated = { ...filters, [key]: value };

    // Reset transaction status if request status is not APPROVED
    if (key === 'requestStatus' && value !== 'APPROVED') {
      updated.transactionStatus = '';
    }

    setFilters(updated);

    onApply({
      originAccountId: updated.originAccountId ? Number(updated.originAccountId) : undefined,
      counterpartyId: updated.counterpartyId ? Number(updated.counterpartyId) : undefined,
      status: updated.requestStatus || undefined,
      transactionStatus: updated.requestStatus === 'APPROVED' && updated.transactionStatus ? updated.transactionStatus : undefined,
      createdAtFrom: updated.createdAtFrom?.toISO(),
      createdAtTo: updated.createdAtTo?.toISO(),
    });
  };

  const handleDateRangeChange = (from: DateTime | null, to: DateTime | null) => {
    const updated = {
      ...filters,
      createdAtFrom: from,
      createdAtTo: to,
    };

    setFilters(updated);

    onApply({
      originAccountId: updated.originAccountId ? Number(updated.originAccountId) : undefined,
      counterpartyId: updated.counterpartyId ? Number(updated.counterpartyId) : undefined,
      status: updated.requestStatus || undefined,
      transactionStatus: updated.requestStatus === 'APPROVED' && updated.transactionStatus ? updated.transactionStatus : undefined,
      createdAtFrom: from?.toISO(),
      createdAtTo: to?.toISO(),
    });
  };

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {/* Date range */}
      <Grid item xs={12 / 5}>
        <DateRangePicker
          from={filters.createdAtFrom}
          to={filters.createdAtTo}
          onChange={(from, to) => {
            handleDateRangeChange(from, to);
          }}
        />
      </Grid>

      {/* Withdrawal Status */}
      <Grid item xs={12 / 5}>
        <FormControl fullWidth size="small">
          <InputLabel>Status Solicitação</InputLabel>
          <Select value={filters.requestStatus} label="Status Solicitação" onChange={(e) => handleChange('requestStatus', e.target.value)}>
            {REQUEST_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Transaction Status */}
      <Grid item xs={12 / 5}>
        <FormControl fullWidth size="small">
          <InputLabel>Status Transação</InputLabel>
          <Select
            value={filters.transactionStatus}
            label="Status Transação"
            onChange={(e) => handleChange('transactionStatus', e.target.value)}
            disabled={filters.requestStatus !== 'APPROVED'}
          >
            {TRANSACTION_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Account */}
      <Grid item xs={12 / 5}>
        <Autocomplete
          options={accounts}
          getOptionLabel={(option) => `${formatTaxNumber(option.cnpj)} - ${option.businessName}`}
          value={accounts.find((a) => String(a.id) === String(filters.originAccountId)) || null}
          onChange={(_, value) => handleChange('originAccountId', value ? value.id : '')}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography fontSize={14} fontWeight={600}>
                  {formatTaxNumber(option.cnpj)}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {option.businessName}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Origem"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingFilters ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              disabled={loadingFilters}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={loadingFilters}
        />
      </Grid>

      {/* Counterparty */}
      <Grid item xs={12 / 5}>
        <Autocomplete
          options={counterparties}
          getOptionLabel={(option) => `${formatTaxNumber(option.cnpj)} - ${option.businessName}`}
          value={counterparties.find((c) => String(c.id) === String(filters.counterpartyId)) || null}
          onChange={(_, value) => handleChange('counterpartyId', value ? value.id : '')}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography fontSize={14} fontWeight={600}>
                  {formatTaxNumber(option.cnpj)}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {option.businessName}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Destinatário"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingFilters ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              disabled={loadingFilters}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={loadingFilters}
        />
      </Grid>
    </Grid>
  );
};

export default ManualWithdrawFilters;
